import { useEffect, useState } from 'react';
import { getControlSchema } from './controlSchema';
import {
  buildGlobalSavePayload,
  buildPersonalSavePayload,
  fieldLabel,
  globalToForm,
  personalSupportsMinMax,
  personalToForm,
  validateGlobalForm,
  validatePersonalForm,
} from './controlFormUtils';

function ModalShell({ open, title, onClose, children, footer }) {
  if (!open) {
    return null;
  }

  return (
    <div className="admin-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="admin-modal admin-modal--wide"
        role="dialog"
        aria-modal="true"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="admin-modal__header">
          <h3>{title}</h3>
          <button type="button" className="admin-modal__close" onClick={onClose} aria-label="關閉">
            ×
          </button>
        </div>
        <div className="admin-modal__body admin-modal__body--form">{children}</div>
        {footer && <div className="admin-modal__footer admin-modal__footer--center">{footer}</div>}
      </div>
    </div>
  );
}

function DirectionField({ form, schema, onChange }) {
  if (!schema?.directionOptions) {
    return null;
  }
  return (
    <div className="form-field">
      <label htmlFor="controlDirection">{fieldLabel('direction')}</label>
      <select
        id="controlDirection"
        value={form.direction}
        onChange={(event) => onChange('direction', event.target.value)}
      >
        {schema.directionOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function EnabledField({ form, onChange }) {
  return (
    <div className="form-field">
      <label htmlFor="controlEnabled">{fieldLabel('enabled')}</label>
      <select
        id="controlEnabled"
        value={form.enabled ? 'true' : 'false'}
        onChange={(event) => onChange('enabled', event.target.value === 'true')}
      >
        <option value="true">啟用</option>
        <option value="false">禁用</option>
      </select>
    </div>
  );
}

function NumberField({ fieldKey, form, onChange, suffix }) {
  return (
    <div className="form-field">
      <label htmlFor={`control-${fieldKey}`}>{fieldLabel(fieldKey)}</label>
      <div className="input-with-suffix">
        <input
          id={`control-${fieldKey}`}
          type="number"
          step="any"
          value={form[fieldKey] ?? ''}
          onChange={(event) => onChange(fieldKey, event.target.value)}
        />
        {suffix && <span className="input-with-suffix__suffix">{suffix}</span>}
      </div>
    </div>
  );
}

export function OrderControlPersonalModal({
  open,
  playType,
  record,
  onClose,
  onSubmit,
  submitting,
}) {
  const schema = getControlSchema(playType, 'personal');
  const [form, setForm] = useState(() => personalToForm(record, playType));
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setForm(personalToForm(record, playType));
      setError('');
    }
  }, [open, record, playType]);

  function updateField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    const validationError = validatePersonalForm(form, playType);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError('');
    await onSubmit(buildPersonalSavePayload(form, playType, record));
  }

  return (
    <ModalShell
      open={open}
      title={record ? '修改控獎' : '新增控獎'}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>
            取消
          </button>
          <button type="submit" form="order-control-personal-form" className="btn btn-primary" disabled={submitting}>
            {submitting ? '保存中…' : '保存'}
          </button>
        </>
      )}
    >
      <form id="order-control-personal-form" onSubmit={handleSubmit}>
        {error && <div className="alert alert-error">{error}</div>}
        <div className="form-grid">
          <div className="form-field">
            <label htmlFor="controlMemberId">{fieldLabel('memberId')}</label>
            <input
              id="controlMemberId"
              type="text"
              inputMode="numeric"
              value={form.memberId}
              disabled={Boolean(record?.id)}
              onChange={(event) => updateField('memberId', event.target.value)}
              placeholder="請輸入用戶 ID"
            />
          </div>
          <DirectionField form={form} schema={schema} onChange={updateField} />
          {personalSupportsMinMax(playType) && (
            <>
              <NumberField
                fieldKey="minValue"
                form={form}
                onChange={updateField}
                suffix={playType === 'TIME_LIMITED' ? '%' : undefined}
              />
              <NumberField
                fieldKey="maxValue"
                form={form}
                onChange={updateField}
                suffix={playType === 'TIME_LIMITED' ? '%' : undefined}
              />
            </>
          )}
          <EnabledField form={form} onChange={updateField} />
        </div>
      </form>
    </ModalShell>
  );
}

export function OrderControlGlobalModal({
  open,
  playType,
  record,
  onClose,
  onSubmit,
  submitting,
}) {
  const schema = getControlSchema(playType, 'global');
  const [form, setForm] = useState(() => globalToForm(record, playType));
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setForm(globalToForm(record, playType));
      setError('');
    }
  }, [open, record, playType]);

  function updateField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    const validationError = validateGlobalForm(form, playType);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError('');
    await onSubmit(buildGlobalSavePayload(form, playType, record));
  }

  return (
    <ModalShell
      open={open}
      title="修改全局控獎"
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>
            取消
          </button>
          <button type="submit" form="order-control-global-form" className="btn btn-primary" disabled={submitting}>
            {submitting ? '保存中…' : '保存'}
          </button>
        </>
      )}
    >
      <form id="order-control-global-form" onSubmit={handleSubmit}>
        {error && <div className="alert alert-error">{error}</div>}
        {schema?.hints?.map((hint) => (
          <p key={hint} className="form-hint form-hint--warning">{hint}</p>
        ))}
        <div className="form-grid">
          {playType === 'FUTURES' ? (
            <>
              <EnabledField form={form} onChange={updateField} />
              <div className="form-field">
                <label htmlFor="controlIsRealOdds">{fieldLabel('isRealOdds')}</label>
                <select
                  id="controlIsRealOdds"
                  value={form.isRealOdds}
                  onChange={(event) => updateField('isRealOdds', event.target.value)}
                >
                  {(schema?.oddsOptions || []).map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              {form.isRealOdds === 'false' && (
                <>
                  <NumberField fieldKey="minRandomOdds" form={form} onChange={updateField} suffix="%" />
                  <NumberField fieldKey="maxRandomOdds" form={form} onChange={updateField} suffix="%" />
                </>
              )}
              <NumberField fieldKey="startKillAmount" form={form} onChange={updateField} suffix="USDT" />
              <NumberField fieldKey="killProbability" form={form} onChange={updateField} suffix="%" />
            </>
          ) : (
            <>
              <EnabledField form={form} onChange={updateField} />
              {form.enabled && (
                <>
                  <DirectionField form={form} schema={schema} onChange={updateField} />
                  <NumberField
                    fieldKey="minValue"
                    form={form}
                    onChange={updateField}
                    suffix={playType === 'TIME_LIMITED' ? '%' : undefined}
                  />
                  <NumberField
                    fieldKey="maxValue"
                    form={form}
                    onChange={updateField}
                    suffix={playType === 'TIME_LIMITED' ? '%' : undefined}
                  />
                </>
              )}
            </>
          )}
        </div>
      </form>
    </ModalShell>
  );
}
